import { background, black, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Box, CircularProgress, Divider, Stack, StackProps, Typography, TypographyProps, styled } from "@mui/material";
import { ReactNode, useMemo } from "react";

export type TInnerBoxProps = {
  title?: ReactNode;
  bordered?: boolean;
  required?: boolean;
  loading?: boolean;
  rightContent?: ReactNode;
  showDivider?: boolean;
  borderColor?: string;
  variantTitle?: TypographyProps['variant'];
} & Omit<StackProps, "title">;

export const InnerBox: FCC<TInnerBoxProps> = ({
  title,
  children,
  bordered = true,
  padding = "24px",
  required = false,
  loading,
  rightContent,
  showDivider = true,
  borderColor = divider.middle,
  variantTitle = "sub16Semi",
  gap,
  ...rest
}) => {
  const renderTitle = useMemo(() => {
    if (!title) return null;
    if (typeof title === "string") {
      return (
        <Typography variant={variantTitle} color={text.primary}>
          {title}
        </Typography>
      );
    }
    return title;
  }, [title]);

  return (
    <InnerBoxWrap
      border={bordered ? "1px solid" : "none"}
      padding={padding}
      borderColor={borderColor}
      gap={(gap ?? "10px") as StackProps['gap']}
      {...rest}
    >
      {(renderTitle || required) && (
        <Stack direction="row" gap={1} alignItems="center">
          {renderTitle}
          {required && <RequiredTag variant="cap12">必須</RequiredTag>}
        </Stack>
      )}
      {showDivider && (
        <Divider
          sx={{
            borderColor: `${black[95]}`,
          }}
        ></Divider>
      )}
      {rightContent && <RightContentWrap>{rightContent}</RightContentWrap>}
      <Box position="relative">
        {loading ? (
          <CenterBox>
            <CircularProgress sx={{ color: text.tertiary }} size="24px" />
          </CenterBox>
        ) : (
          children
        )}
      </Box>
    </InnerBoxWrap>
  );
};

const InnerBoxWrap = styled(Stack)<{ borderColor: string }>`
  position: relative;
  border-color: ${(props) => props.borderColor};
  border-radius: ${rounded.sm};
  background: ${background.white};
`;

const RightContentWrap = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 24px;
`;

const RequiredTag = styled(Typography)`
  height: 20px;
  width: 40px;
  text-align: center;
  border: 1px solid ${text.error};
  color: ${text.error};
  border-radius: ${rounded.xs};
`;

const CenterBox = styled(Box)`
  background-color: ${background.white};
  height: 100%;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
